/**
 * `/finance/refunds` — refund queue (age, amount, owner, attempts, next action, participant-safe explanation),
 * refund a payment with a reason (step-up), and the cancelled-lottery refund wave (dry run → typed confirmation →
 * execute with aggregate progress). §10.4, §16.3.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefundReason, type PaymentAdminRowDto, type RefundWaveDto } from '@windrop/contracts';
import { Button, Field, Input, Modal, Select, useIntl, useToast } from '@windrop/ui';
import { useState } from 'react';
import { Can } from '../../auth/guards';
import { useStepUp } from '../../auth/step-up';
import { ConfirmDestructive } from '../../components/ConfirmDestructive';
import { SectionTitle } from '../../components/Card';
import { DataTable } from '../../components/DataTable';
import { FilterBar, FilterField } from '../../components/FilterBar';
import { PageHeader } from '../../components/PageHeader';
import { QueueList } from '../../components/QueueList';
import { StatusBadge, type Tone } from '../../components/StatusBadge';
import { financeApi } from '../../lib/api/finance';
import { isApiError } from '../../lib/api/client';
import { FinanceNav } from './FinanceNav';

export function RefundsPage() {
  const intl = useIntl();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { withStepUp } = useStepUp();
  const [lotteryId, setLotteryId] = useState('');
  const [target, setTarget] = useState<PaymentAdminRowDto | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<RefundReason>('PARTICIPANT_REQUEST');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [wave, setWave] = useState<RefundWaveDto | null>(null);
  const [confirmWave, setConfirmWave] = useState(false);
  const refunds = useQuery({
    queryKey: ['finance', 'refunds', lotteryId],
    queryFn: () => financeApi.refunds(lotteryId ? { lotteryId } : undefined),
  });
  const payments = useQuery({
    queryKey: ['finance', 'payments', lotteryId],
    queryFn: () => financeApi.payments({ ...(lotteryId ? { lotteryId } : {}), limit: 50 }),
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['finance'] });
  const refund = useMutation({
    mutationFn: () =>
      withStepUp(
        () =>
          financeApi.createRefund({
            paymentId: target!.paymentId,
            reason,
            ...(amount ? { amount: { amountMinor: amount, currency: target!.amount.currency } } : {}),
            ...(note ? { note } : {}),
          }),
        { permission: 'finance.refund', reason: `Refunding payment ${target?.paymentId}.` },
      ),
    onSuccess: (r) => {
      setTarget(null);
      setAmount('');
      setNote('');
      invalidate();
      toast({ title: `Refund ${r.status.toLowerCase()} — ${r.entryEffect}`, tone: 'success' });
    },
    onError: (e) => {
      if (e instanceof Error && e.name === 'StepUpCancelled') return;
      setError(isApiError(e) ? e.message : 'Refund failed.');
    },
  });
  const dryRun = useMutation({
    mutationFn: () =>
      withStepUp(() => financeApi.refundWave(lotteryId, { dryRun: true }), {
        permission: 'finance.refund',
        reason: 'Refund wave dry run.',
      }),
    onSuccess: setWave,
    onError: (e) => toast({ title: isApiError(e) ? e.message : 'Dry run failed', tone: 'neutral' }),
  });
  const execute = useMutation({
    mutationFn: (r: string) =>
      withStepUp(() => financeApi.refundWave(lotteryId, { dryRun: false, note: r }), {
        permission: 'finance.refund',
        reason: 'Execute refund wave.',
      }),
    onSuccess: (r) => {
      setWave(r);
      setConfirmWave(false);
      invalidate();
      toast({
        title: `Refund wave: ${r.succeeded} refunded, ${r.failed} failed (${r.progressPercent}%)`,
        tone: 'success',
      });
    },
    onError: (e) => toast({ title: isApiError(e) ? e.message : 'Refund wave failed', tone: 'neutral' }),
  });
  const refundable = (p: PaymentAdminRowDto) =>
    ['SUCCEEDED', 'PARTIALLY_REFUNDED', 'RECONCILIATION_EXCEPTION'].includes(p.status) &&
    BigInt(p.amount.amountMinor) > BigInt(p.refundedAmount.amountMinor);
  return (
    <>
      <PageHeader
        title="Finance — refunds"
        description="Full or partial refunds post reversing journals; linked entries become ineligible before the snapshot; after a win the fulfillment is frozen for review. History is never deleted."
        breadcrumb={[{ label: 'Finance', to: '/finance' }, { label: 'Refunds' }]}
      />
      <FinanceNav />
      <FilterBar>
        <FilterField label="Lottery id" htmlFor="refund-lottery" grow>
          <Input
            id="refund-lottery"
            value={lotteryId}
            onChange={(e) => setLotteryId(e.target.value.trim())}
            placeholder="Filters payments and refunds; required for a refund wave"
            data-testid="refund-lottery"
          />
        </FilterField>
      </FilterBar>
      <section aria-labelledby="refund-queue" className="wd-stack">
        <SectionTitle title={<span id="refund-queue">Refund queue</span>} />
        <QueueList
          items={(refunds.data?.items ?? []).map((r) => ({
            id: r.refundId,
            title: `${intl.formatMoney(r.amount.amountMinor, r.amount.currency)} · ${r.reason.replace(/_/g, ' ').toLowerCase()}`,
            subtitle: `${r.participantSafeExplanation} · attempts ${r.attempts} · payment ${r.paymentId}`,
            since: r.createdAt,
            owner: r.owner,
            status: {
              tone: (r.status === 'COMPLETED'
                ? 'success'
                : r.status === 'FAILED'
                  ? 'error'
                  : 'warning') as Tone,
              text: r.status,
            },
            nextAction: {
              label: r.nextAction ?? 'Open refund',
              to: `/finance/refunds?paymentId=${r.paymentId}`,
            },
          }))}
          emptyText="No refunds yet."
        />
      </section>
      <section aria-labelledby="refund-payments" className="wd-stack">
        <SectionTitle
          title={<span id="refund-payments">Payments</span>}
          description="Masked; refund opens a dialog with the refundable amount."
        />
        <DataTable
          caption="Payments (masked)"
          rows={payments.data?.items ?? []}
          rowKey={(p) => p.paymentId}
          loading={payments.isPending}
          pageSize={25}
          columns={[
            {
              id: 'id',
              header: 'Payment',
              cell: (p) => <code>{p.paymentId.slice(-8)}</code>,
              filterValue: (p) => p.paymentId,
            },
            {
              id: 'status',
              header: 'Status',
              cell: (p) => <StatusBadge kind="PaymentStatus" value={p.status} />,
              sortValue: (p) => p.status,
            },
            {
              id: 'amount',
              header: 'Amount',
              cell: (p) => intl.formatMoney(p.amount.amountMinor, p.amount.currency),
              sortValue: (p) => Number(p.amount.amountMinor),
              align: 'end',
            },
            {
              id: 'refunded',
              header: 'Refunded',
              cell: (p) => intl.formatMoney(p.refundedAmount.amountMinor, p.refundedAmount.currency),
              align: 'end',
            },
            { id: 'entries', header: 'Entries', cell: (p) => p.entryIds.length, align: 'end' },
            {
              id: 'created',
              header: 'Created',
              cell: (p) => intl.formatDateTime(p.createdAt),
              sortValue: (p) => p.createdAt,
            },
          ]}
          rowActions={(p) => (
            <Can permission="finance.refund">
              <Button
                variant="secondary"
                disabled={!refundable(p)}
                onClick={() => {
                  setError(null);
                  setTarget(p);
                }}
              >
                Refund
              </Button>
            </Can>
          )}
        />
      </section>
      <Can permission="finance.refund">
        <section aria-labelledby="refund-wave" className="wdc-panel">
          <h2 id="refund-wave">Cancelled lottery — refund wave</h2>
          <p className="wd-muted">
            Dry run first: the count and total are shown before anything moves. Execution is idempotent;
            progress is published to participants (§10.4).
          </p>
          <div className="wd-btn-group">
            <Button
              variant="secondary"
              disabled={!lotteryId}
              onClick={() => dryRun.mutate()}
              loading={dryRun.isPending}
              loadingLabel="Counting"
            >
              Dry run
            </Button>
            <Button disabled={!wave || wave.eligible === 0} onClick={() => setConfirmWave(true)}>
              Execute refund wave
            </Button>
          </div>
          {wave ? (
            <dl className="wdc-facts">
              <div>
                <dt>Eligible</dt>
                <dd>
                  {wave.eligible} ·{' '}
                  {intl.formatMoney(wave.eligibleAmount.amountMinor, wave.eligibleAmount.currency)}
                </dd>
              </div>
              <div>
                <dt>Already refunded</dt>
                <dd>{wave.alreadyRefunded}</dd>
              </div>
              <div>
                <dt>Progress</dt>
                <dd>{wave.progressPercent}%</dd>
              </div>
              {!wave.dryRun ? (
                <div>
                  <dt>Last execution</dt>
                  <dd>
                    {wave.succeeded} refunded, {wave.failed} failed
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}
        </section>
      </Can>
      <ConfirmDestructive
        open={confirmWave}
        onOpenChange={setConfirmWave}
        title="Execute refund wave"
        objectName={lotteryId}
        objectKind="lottery id"
        consequence="Every confirmed paid entry of this cancelled lottery is refunded in full at the provider and becomes REFUNDED. Participants see the aggregate progress."
        affected={
          wave
            ? {
                affected: wave.eligible,
                detail: intl.formatMoney(wave.eligibleAmount.amountMinor, wave.eligibleAmount.currency),
              }
            : null
        }
        requireReason
        busy={execute.isPending}
        onConfirm={({ reason: r }) => execute.mutate(r ?? '')}
      />
      <Modal
        open={target !== null}
        onOpenChange={(o) => {
          if (!o) setTarget(null);
        }}
        title="Refund payment"
        description={
          target
            ? `Refundable: ${intl.formatMoney((BigInt(target.amount.amountMinor) - BigInt(target.refundedAmount.amountMinor)).toString(), target.amount.currency)}`
            : undefined
        }
      >
        <div className="wd-stack">
          <Field label="Amount (minor units)" hint="Leave empty for a full refund.">
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              data-testid="refund-amount"
            />
          </Field>
          <Field label="Reason" required>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value as RefundReason)}
              options={RefundReason.options.map((r) => ({
                value: r,
                label: r.replace(/_/g, ' ').toLowerCase(),
              }))}
            />
          </Field>
          <Field label="Note">
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {error ? (
            <p role="alert" className="text-error">
              {error}
            </p>
          ) : null}
          <div className="wd-btn-group">
            <Button onClick={() => refund.mutate()} loading={refund.isPending} loadingLabel="Refunding">
              Refund with this reason
            </Button>
            <Button variant="secondary" onClick={() => setTarget(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
